import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { MonthPicker, currentMonth } from "@/components/MonthPicker";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { MapPinned } from "lucide-react";

export function ConfrontoProvince() {
  const [mese, setMese] = useState(currentMonth());
  const { data } = useQuery({ queryKey: ["dashboard-province", mese], queryFn: async () => (await api.get("/dashboard/province", { params: { mese } })).data });
  const rows = (data?.province || []).map((p) => ({ ...p, label: p.provincia || "N/D" }));
  return (
    <Card className="mt-6 border-slate-200 shadow-sm" data-testid="confronto-province-card">
      <CardContent className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50"><MapPinned className="h-5 w-5 text-[#166534]" /></div>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">Confronto tra province</h2>
              <p className="text-xs text-slate-500">Ore di servizio e servizi completati nel mese</p>
            </div>
          </div>
          <MonthPicker value={mese} onChange={setMese} testid="confronto-province-month" />
        </div>
        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="confronto-province-empty">Nessun turno nel mese selezionato.</p>
        ) : (
          <>
            <div className="h-72 w-full" data-testid="confronto-province-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} maxBarSize={56} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip labelFormatter={(_, p) => p?.[0]?.payload?.nome || ""} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ore" name="Ore di servizio" fill="#166534" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="servizi" name="Servizi completati" fill="#D97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="confronto-province-table">
                <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="py-2 pr-4">Provincia</th><th className="py-2 pr-4">Guardie</th><th className="py-2 pr-4">Turni</th><th className="py-2 pr-4">Servizi completati</th><th className="py-2">Ore</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.label} data-testid={`confronto-row-${r.label}`}>
                      <td className="py-2 pr-4 font-medium text-slate-900">{r.nome}{r.provincia ? ` (${r.provincia})` : ""}</td>
                      <td className="py-2 pr-4">{r.guardie}</td><td className="py-2 pr-4">{r.turni}</td><td className="py-2 pr-4">{r.servizi}</td><td className="py-2 font-semibold">{r.ore}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
